import React from 'react';
import RiskIndicator from './RiskIndicator';

/**
 * TherapistClientList - Displays list of therapist's clients with boundary status
 */
const TherapistClientList = ({ clients, summary, onSelectClient, selectedClientId, loading }) => {
  const cardStyle = {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px'
  };

  const headerStyle = {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '16px',
    color: '#333',
    borderBottom: '1px solid #eee',
    paddingBottom: '8px'
  };

  const summaryStyle = {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    flexWrap: 'wrap'
  };

  const summaryItemStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '80px'
  };

  const clientItemStyle = (isSelected) => ({
    padding: '12px',
    marginBottom: '8px',
    backgroundColor: isSelected ? '#e3f2fd' : '#f8f9fa',
    borderRadius: '6px',
    cursor: 'pointer',
    border: isSelected ? '2px solid #2196f3' : '1px solid transparent',
    transition: 'all 0.2s ease'
  });

  const flagTagStyle = (type) => {
    const isCBC = type.includes('drift') || type.includes('therapeutic') ||
                  type.includes('role') || type.includes('resistance') || type.includes('rupture');
    return {
      display: 'inline-block',
      padding: '2px 6px',
      fontSize: '10px',
      borderRadius: '4px',
      marginRight: '4px',
      marginTop: '4px',
      backgroundColor: isCBC ? '#cce5ff' : '#fff3cd',
      color: isCBC ? '#004085' : '#856404'
    };
  };

  const flagTypeShortNames = {
    cbc_persistent_drift: 'Drift',
    cbc_low_therapeutic_engagement: 'Low Engagement',
    cbc_role_abandonment_pattern: 'Role Abandon',
    cbc_client_resistance_pattern: 'Resistance',
    cbc_therapeutic_rupture: 'Rupture',
    cbc_attachment_language_pattern: 'Attachment',
    cbc_high_engagement_frequency: 'High Freq',
    cbc_critical_engagement_frequency: 'Critical Freq',
    cbc_dependency_escalation: 'Dependency',
    cbc_social_isolation_indicators: 'Isolation',
    cbc_delusional_attribution: 'Delusional'
  };

  if (loading) {
    return (
      <div style={cardStyle}>
        <div style={headerStyle}>My Clients - Boundary Monitoring</div>
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          Loading clients...
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>My Clients - Boundary Monitoring</div>

      {/* Summary Section */}
      {summary && (
        <div style={summaryStyle}>
          <div style={summaryItemStyle}>
            <span style={{ fontSize: '24px', fontWeight: '700', color: '#333' }}>
              {summary.totalClients || 0}
            </span>
            <span style={{ fontSize: '11px', color: '#666' }}>Total Clients</span>
          </div>
          <div style={summaryItemStyle}>
            <span style={{ fontSize: '24px', fontWeight: '700', color: '#dc3545' }}>
              {summary.atRisk?.critical || 0}
            </span>
            <span style={{ fontSize: '11px', color: '#666' }}>Critical</span>
          </div>
          <div style={summaryItemStyle}>
            <span style={{ fontSize: '24px', fontWeight: '700', color: '#fd7e14' }}>
              {summary.atRisk?.high || 0}
            </span>
            <span style={{ fontSize: '11px', color: '#666' }}>High</span>
          </div>
          <div style={summaryItemStyle}>
            <span style={{ fontSize: '24px', fontWeight: '700', color: '#ffc107' }}>
              {summary.atRisk?.medium || 0}
            </span>
            <span style={{ fontSize: '11px', color: '#666' }}>Medium</span>
          </div>
          <div style={summaryItemStyle}>
            <span style={{ fontSize: '24px', fontWeight: '700', color: '#28a745' }}>
              {summary.atRisk?.low || 0}
            </span>
            <span style={{ fontSize: '11px', color: '#666' }}>Low</span>
          </div>
        </div>
      )}

      {/* Client List */}
      {(!clients || clients.length === 0) ? (
        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          No clients with boundary concerns.
        </div>
      ) : (
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {clients.map((client) => (
            <div
              key={client.clientId}
              style={clientItemStyle(selectedClientId === client.clientId)}
              onClick={() => onSelectClient(client.clientId)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    {client.clientName || 'Unknown Client'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {client.clientEmail}
                  </div>
                </div>
                <RiskIndicator level={client.riskLevel} size="small" />
              </div>

              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: '#666' }}>
                <span>Flags: {client.activeFlags}</span>
                <span>Signals: {client.totalSignals || 0}</span>
                {client.maxAttachmentScore > 0 && (
                  <span>Attachment: {(client.maxAttachmentScore * 100).toFixed(0)}%</span>
                )}
              </div>

              {client.flagTypes && client.flagTypes.length > 0 && (
                <div style={{ marginTop: '6px' }}>
                  {client.flagTypes.map((type) => (
                    <span key={type} style={flagTagStyle(type)}>
                      {flagTypeShortNames[type] || type.replace('cbc_', '').replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}

              {client.lastConversation && (
                <div style={{ marginTop: '6px', fontSize: '11px', color: '#888' }}>
                  Last conversation: {new Date(client.lastConversation).toLocaleDateString()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TherapistClientList;
